import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import { Icon } from '@/design-system/assets/icons/Icon.jsx';
import { cn } from '../../utils/cn';

interface SelectContextType {
  isOpen: boolean;
  selectedValue: string;
  onValueChange: (value: string) => void;
  onOpenChange: (open: boolean) => void;
}

const SelectContext = createContext<SelectContextType | null>(null);

const useSelectContext = () => {
  const context = useContext(SelectContext);
  if (!context) {
    throw new Error('Select components must be used within a Select');
  }
  return context;
};

interface SelectProps {
  value?: string;
  onValueChange?: (value: string) => void;
  children: React.ReactNode;
  className?: string;
}

export const Select: React.FC<SelectProps> = ({
  value = '',
  onValueChange,
  children,
  className
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value);

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  const handleValueChange = (newValue: string) => {
    setSelectedValue(newValue);
    onValueChange?.(newValue);
    setIsOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  return (
    <SelectContext.Provider value={{
      isOpen,
      selectedValue,
      onValueChange: handleValueChange,
      onOpenChange: handleOpenChange
    }}>
      <div className={cn('relative w-full', className)}>
        {children}
      </div>
    </SelectContext.Provider>
  );
};

interface SelectTriggerProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'onClick' | 'disabled'> {
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
}

export const SelectTrigger: React.FC<SelectTriggerProps> = ({
  children,
  className,
  disabled = false,
  ...rest
}) => {
  const { isOpen, onOpenChange } = useSelectContext();
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current && !triggerRef.current.contains(target)) {
        const selectContent = document.querySelector('[data-select-content]');
        if (selectContent && selectContent.contains(target)) {
          return;
        }
        onOpenChange(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onOpenChange]);

  return (
    <button
      ref={triggerRef}
      {...rest}
      type="button"
      className={cn(
        'flex h-8 w-full items-center justify-between rounded-md border border-border bg-background px-3 py-1.5 text-sm',
        'placeholder:text-muted-foreground/50',
        'transition-all duration-150',
        'focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/50', // Refined focus
        'disabled:cursor-not-allowed disabled:opacity-50',
        'shadow-[0_1px_2px_rgba(0,0,0,0.05)]',
        className
      )}
      onClick={() => !disabled && onOpenChange(!isOpen)}
      disabled={disabled}
    >
      {children}
      <Icon name="KeyboardArrowDown" size={14} className={cn(
        'opacity-50 transition-transform duration-200',
        isOpen && 'rotate-180'
      )} />
    </button>
  );
};

interface SelectValueProps {
  placeholder?: string;
  className?: string;
}

export const SelectValue: React.FC<SelectValueProps> = ({
  placeholder = '선택하세요',
  className
}) => {
  const { selectedValue } = useSelectContext();

  return (
    <span className={cn('block truncate text-sm', className)}>
      {selectedValue || placeholder}
    </span>
  );
};

interface SelectContentProps {
  children: React.ReactNode;
  className?: string;
}

export const SelectContent: React.FC<SelectContentProps> = ({
  children,
  className
}) => {
  const { isOpen } = useSelectContext();

  if (!isOpen) return null;

  return (
    <div
      data-select-content
      className={cn(
        'absolute top-[calc(100%+4px)] left-0 right-0 z-[9999]',
        'max-h-60 w-full overflow-auto rounded-md border border-border',
        'bg-popover text-popover-foreground',
        'shadow-lg shadow-black/5',
        'animate-in fade-in-0 zoom-in-95 duration-100', // Linear-like popover animation
        className
      )}
    >
      <div className="p-1">
        {children}
      </div>
    </div>
  );
};

interface SelectItemProps {
  value: string;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
}

export const SelectItem: React.FC<SelectItemProps> = ({
  value,
  children,
  className,
  disabled = false
}) => {
  const { selectedValue, onValueChange } = useSelectContext();
  const isSelected = selectedValue === value;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      onValueChange(value);
    }
  };

  return (
    <button
      type="button"
      className={cn(
        'relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none',
        'transition-colors duration-100', // Fast hover
        'hover:bg-accent hover:text-accent-foreground',
        'focus:bg-accent focus:text-accent-foreground',
        'disabled:pointer-events-none disabled:opacity-50',
        isSelected && 'bg-accent/50 text-accent-foreground font-medium', // Highlight selected
        className
      )}
      onClick={handleClick}
      disabled={disabled}
    >
      <span className="flex-1 truncate text-left">{children}</span>
      {isSelected && (
        <Icon name="Check" size={14} className="ml-auto opacity-80" />
      )}
    </button>
  );
};
